import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactToPrint from "react-to-print";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";
import "../css/momFormat.css";

const API_URL = process.env.REACT_APP_API_URL;

const ViewMomFormat = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const [rec, setRec] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const token = await localforage.getItem("token");
        const { data } = await api.get(`${API_URL}/mom-format/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRec(data);
      } catch (e) {
        setError("Could not load this meeting record.");
      }
    })();
  }, [id]);

  if (error)
    return (
      <>
        <Dashboard />
        <div className="momf-page">
          <p style={{ textAlign: "center" }}>{error}</p>
        </div>
      </>
    );
  if (!rec)
    return (
      <>
        <Dashboard />
        <div className="momf-page">
          <p style={{ textAlign: "center" }}>Loading…</p>
        </div>
      </>
    );

  const s = rec.stakeholders || [];
  const showParty = rec.state === "Punjab" || s.some((x) => x.party);
  const disc = rec.discussionPoints || [];
  const stc = rec.stcActionables || [];
  const leader = rec.leaderActionables || [];

  return (
    <>
      <Dashboard />
      <div className="momf-page">
        <div className="momf-head">
          <button className="momf-back-btn" onClick={() => navigate("/mom-dashboard")}>
            ← Back to Dashboard
          </button>
          <ReactToPrint
            trigger={() => (
              <button type="button" className="momf-pdf-btn">
                ⬇ Download PDF
              </button>
            )}
            content={() => printRef.current}
            pageStyle={`@page { size: A4 portrait; margin: 14mm 12mm; }`}
            documentTitle="Minutes of Meeting"
            removeAfterPrint={true}
          />
        </div>

        <div className="momf-preview-wrap">
          <div className="mom-doc" ref={printRef}>
            <h1 className="mom-doc-title">
              Minutes of Meeting (MOM) – Standard Format
            </h1>

            <table className="mom-grid">
              <tbody>
                <tr>
                  <td className="lbl">Meeting Date</td>
                  <td className="val">{rec.meetingDate}</td>
                  <td className="lbl">No. of Stakeholders</td>
                  <td className="val">{rec.numStakeholders}</td>
                  <td className="lbl" rowSpan={2}>
                    Location
                    <br />
                    Capture
                  </td>
                  <td className="val" rowSpan={2}>
                    {rec.location}
                    {rec.gMapLocation ? (
                      <>
                        <br />
                        <a
                          href={`https://www.google.com/maps?q=${rec.gMapLocation}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View on Google Maps
                        </a>
                      </>
                    ) : null}
                  </td>
                </tr>
                <tr>
                  <td className="lbl">Meeting Time</td>
                  <td className="val">{rec.meetingTime}</td>
                  <td className="lbl">Tentative Duration (in hrs)</td>
                  <td className="val">{rec.duration}</td>
                </tr>
              </tbody>
            </table>

            <div className="mom-section-title">Stakeholders Details:</div>
            <table className="mom-table-doc">
              <thead>
                <tr>
                  <th>Stakeholder Name</th>
                  <th>Designation</th>
                  {showParty && <th>Party</th>}
                  <th>Phone Number</th>
                </tr>
              </thead>
              <tbody>
                {s.map((x, i) => (
                  <tr key={i}>
                    <td>{x.name}</td>
                    <td>{x.designation}</td>
                    {showParty && <td>{x.party || "—"}</td>}
                    <td>{x.phone}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mom-section-title">Key Points of Discussion:</div>
            <table className="mom-table-doc">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>
                    Key Discussion Point
                    <br />
                    <span className="sub">
                      (Issue / Feedback / Demands / Suggestion)
                    </span>
                  </th>
                  <th>Details of Discussion</th>
                </tr>
              </thead>
              <tbody>
                {disc.map((d, i) => (
                  <tr key={i}>
                    <td className="num">{i + 1}</td>
                    <td>{d.point}</td>
                    <td>{d.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mom-section-title">Actionables – STC POC:</div>
            <table className="mom-table-doc">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Issue</th>
                  <th>Detailed Actionable</th>
                  <th>Expected Timeline</th>
                </tr>
              </thead>
              <tbody>
                {stc.map((a, i) => (
                  <tr key={i}>
                    <td className="num">{i + 1}</td>
                    <td>{a.issue}</td>
                    <td>{a.actionable}</td>
                    <td>{a.timeline}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mom-section-title">Actionables – Party Leader:</div>
            <table className="mom-table-doc">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Issue / Action Point</th>
                  <th>Detailed Actionable</th>
                  <th>Concerned Party Leader</th>
                  <th>Expected Timeline</th>
                </tr>
              </thead>
              <tbody>
                {leader.map((a, i) => (
                  <tr key={i}>
                    <td className="num">{i + 1}</td>
                    <td>{a.issue}</td>
                    <td>{a.actionable}</td>
                    <td>{a.partyLeader}</td>
                    <td>{a.timeline}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <table className="mom-grid mom-grid-bottom">
              <tbody>
                <tr>
                  <td className="lbl">Next Meeting (Tentative)</td>
                  <td className="val">{rec.nextMeetingDate}</td>
                </tr>
                <tr>
                  <td className="lbl remarks-lbl">Remarks/Comments/Notes</td>
                  <td className="val remarks-val">{rec.remarks}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewMomFormat;
